/**
 * Whether to offer this team its own Pro trial, and for how long.
 *
 * Two places ask: the nav bar, which shows a "Start Your Free Pro Trial!" button where the Pro
 * features button would otherwise be, and the upgrade modal, which offers the trial under the buy
 * button. They must agree, and they must not each hit the API — so the answer is fetched once per
 * team and shared.
 *
 * The answer depends on team state and on trial history, neither of which the client knows:
 * whether the team is already Pro, already on or lapsed from a trial, a guest team, or has used
 * its one self-started trial already. The server decides; this only caches and distributes.
 */

import { useEffect, useState } from 'react';
import { useAuthContext } from '../contexts/AuthContext';
import { useFeatureFlag } from './useFeatureFlag';
import { teamApiService } from '../services/teamApiService';

export interface ProTrialSelfSignupOffer {
    /** True only when the team may actually start one. Gate the offer on this. */
    canOffer: boolean;
    /** How long the trial runs, from the server, so no caller names its own number. */
    weeks: number;
}

interface EligibilityResult {
    eligible: boolean;
    weeks: number;
}

/**
 * One in-flight request per team, shared by every caller.
 *
 * Not invalidated: starting a trial force-logs-out the whole team, which tears the app down, so
 * a stale "eligible" cannot survive its own answer.
 */
const cache = new Map<string, Promise<EligibilityResult>>();

function fetchEligibility(teamId: string): Promise<EligibilityResult> {
    const existing = cache.get(teamId);
    if (existing) return existing;

    const request = teamApiService.getSelfServiceTrialEligibility()
        .then((result) => ({ eligible: result.eligible, weeks: result.weeks }))
        .catch(() => {
            // A failure means no offer, not a broken page. Drop it from the cache so a later
            // mount can try again rather than being told "no" for the rest of the session.
            cache.delete(teamId);
            return { eligible: false, weeks: 0 };
        });

    cache.set(teamId, request);
    return request;
}

export function useProTrialSelfSignupOffer(): ProTrialSelfSignupOffer {
    // Both flags, read unconditionally. The endpoints require both: without ProTrial nothing
    // would ever end the trial, since ProTrialGate is gated on it.
    const selfSignupEnabled = useFeatureFlag('ProTrialSelfSignup');
    const proTrialEnabled = useFeatureFlag('ProTrial');
    const { user } = useAuthContext();

    const teamId = user?.team?.teamId;
    const isPro = (user?.team?.subscriptionType || 'Standard').toLowerCase() === 'pro';
    const shouldAsk = selfSignupEnabled && proTrialEnabled && !isPro && !!teamId;

    const [offer, setOffer] = useState<ProTrialSelfSignupOffer>({ canOffer: false, weeks: 0 });

    useEffect(() => {
        if (!shouldAsk || !teamId) {
            setOffer({ canOffer: false, weeks: 0 });
            return;
        }

        let cancelled = false;
        fetchEligibility(teamId).then((result) => {
            if (cancelled) return;
            setOffer({ canOffer: result.eligible, weeks: result.weeks });
        });

        return () => { cancelled = true; };
    }, [shouldAsk, teamId]);

    return offer;
}
